import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, PageShell, Pagination, Skeleton } from '@/components/ui';
import { Plus } from 'lucide-react';
import { useUserGradesQuery } from '../hooks/useUserGradesQuery';
import { GradeTable } from '../components/GradeTable';
import { GradeCreateForm } from '../components/GradeCreateForm';

const PAGE_SIZE = 50;

export const UserGradeListPage = () => {
  const { t } = useTranslation('common');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  const query = useUserGradesQuery({ page, size: PAGE_SIZE });
  const items = query.data?.content ?? [];
  const totalElements = query.data?.totalElements ?? 0;

  return (
    <PageShell
      title={t('userGrades.title')}
      actions={
        <Button size="sm" onClick={() => setShowCreate((v) => !v)}>
          <Plus size={14} />
          {showCreate ? t('userGrades.cancel') : t('userGrades.create')}
        </Button>
      }
      data-testid="user-grade-list-page"
    >
      {showCreate && (
        <GradeCreateForm
          onSuccess={() => {
            setShowCreate(false);
            void query.refetch();
          }}
        />
      )}

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[36px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('userGrades.loadFailed')}</p>
          {query.error?.message ? (
            <p className="max-w-md text-xs" style={{ color: 'var(--content-muted)' }}>
              {query.error.message}
            </p>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('userGrades.retry')}
          </Button>
        </div>
      ) : (
        <GradeTable items={items} />
      )}

      <Pagination
        total={totalElements ?? 0}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        className="mt-4"
      />
    </PageShell>
  );
};
